import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TealiumConfiguration } from './tealium-config';
import { VirtualPageProperties, TealiumConstants, DisplayOptionsProperties, MemberProperties, SearchProperties, ResortProperties, OrderProperties, AgentProperties } from './tealium-data-points.model';
import { ResortPageViews } from '../resort-custom-events';
import { UserActivityDataContainer } from '../user-activity-logger.service';
import * as moment from 'moment';
import { ResortInventory } from '../../models/presentation.model';
import { CreditCardType } from '../../shared/core/ovs.core';
import { CardTypes } from '../../models/card-types.enum';

@Injectable()

export class TealiumService {

    static script_src = '';
    static configuration: TealiumConfiguration = new TealiumConfiguration();
    static productname = "Resort";
    static funnelStep = '';

    constructor() {
        (<any>window).utag_cfg_ovrd = (<any>window).utag_cfg_ovrd || {};
        (<any>window).utag_cfg_ovrd = { noview: true };
    }

    static get isEnabled(): boolean {
        //TODO: for testing set to true, must be dynamic unless specifically testing tealium
        return this.configuration.isEnabled === "true";
    }

    static intialize(config: TealiumConfiguration) {
        this.configuration = config;

        if (this.isEnabled) this.setConfig(this.configuration);
    }
    // Generic script loader with callback

    static getScript(src: string, callback: Function) {
        let d = document;
        let o = {
            callback: callback || function () {
            }
        };

        let s, t;


        if (typeof src === 'undefined') {
            return;
        }

        s = d.createElement('script');
        s.language = 'javascript';
        s.type = 'text/javascript';
        s.async = 1;
        s.charset = 'utf-8';
        s.src = src;

        if (typeof o.callback === 'function') {

            if (d.addEventListener) {
                s.addEventListener('load', function () { o.callback(); }, false);
            } else {
                // old IE support

                // tslint:disable-next-line:max-line-length
                s.onreadystatechange = function () { if (this.readyState === 'complete' || this.readyState === 'loaded') { this.onreadystatechange = null; o.callback(); } };
            }
        }

        t = d.getElementsByTagName('script')[1];
        t.parentNode.insertBefore(s, t);

    }

    // Config settings used to build the path to the utag.js file
    protected static setConfig(config: { account: string, profile: string, environment: string }) {

        if (config.account !== undefined && config.profile !== undefined && config.environment !== undefined) {

            this.script_src = `https://tags.tiqcdn.com/utag/${config.account}/${config.profile}/${config.environment}/utag.js`;

            if ((<any>window).utag === undefined) {
                this.getScript(this.script_src, function () {
                });
            }
        }
    }

    // Data layer is optional set of key/value pairs
    static track(tealium_event: string, data?: any) {

        if (this.script_src === "") {
            //console.log('Tealium config not set.');
            return;
        }

        if ((<any>window).utag === undefined) {

            this.getScript(this.script_src, function () {
                (<any>window).utag.track(tealium_event, data);
            });
        } else {
            (<any>window).utag.track(tealium_event, data);
        }

        // Populate query string and pathname for tealium dom object
        var hash = data['dom.hash'];
        if (hash) {
            let title = (<any>window).utag_data['dom.title'];
            (<any>window).utag_data['dom.title'] = title + "|" + this.funnelStep;
            (<any>window).utag_data['dom.pathname'] = data['dom.hash'].split('?')[0];
            (<any>window).utag_data['dom.query_string'] = '?' + data['dom.hash'].split('?')[1].split('#')[0];
        }
    }

    private static update() {
        if (this.isEnabled) {
            setTimeout(() => this.track('view', (<any>window).utag_data), 2000);
        }
    }

    public static logEvent(data?: any) {
        if (this.isEnabled) {
            setTimeout(() => this.track('link', data), 2000);
        }
    }

    public static logPageView(pageName: string, data?: any, additionalData?: any) {

        let pushToTealium = true;
        if (pageName === null || pageName === undefined || pageName === "") {
            pushToTealium = false;
        }

        const pageProperties = new VirtualPageProperties();
        pageProperties.product_name = this.productname;
        pageProperties.page_name = pageName;
        pageProperties.page_type = this.getTealiumPageType(pageName);
        this.funnelStep = this.getTealiumPageType(pageName);
        pageProperties.site_section = this.getSiteSection(pageName);
        pageProperties.date = moment(new Date()).format("MM/DD/YYYY");
        if (data !== null) {
            pageProperties.language_code = data.configuration.Locale;
            pageProperties.country_code = data.configuration.Locale;
        }
        this.setInfo(pageProperties);
        this.publishDataToUtag(pageName, data, additionalData, pushToTealium);
    }

    private static setInfo(info: any) {
        if (this.isEnabled) {
            this.merge(info);
        }
    }

    public static populate(properties: any, forceUpdate?: boolean, pushToTealium?: boolean) {

        if (this.isEnabled) {
            this.merge(properties);
            if (pushToTealium !== null && pushToTealium === false) {
                return;
            }
            if (forceUpdate) {
                this.update();
            }
        }
    }

    private static merge(obj2: any): any {

        if (obj2 !== undefined && obj2 !== null) {
            Object.keys(obj2).forEach(key => {
                if (obj2[key] !== undefined && obj2[key] !== null) { (<any>window).utag_data[key] = obj2[key]; }
            });
        }
    }

    private static removeData(obj2: any): any {

        if (obj2 !== undefined && obj2 !== null) {
            Object.keys(obj2).forEach(key => {
                if (obj2[key] !== undefined && obj2[key] !== null) { delete (<any>window).utag_data[key] }
            });
        }
    }

    static updateMemberProperties(publishedData: UserActivityDataContainer) {

        if (publishedData.configuration !== null && publishedData.configuration !== undefined) {

            const memberProperties = new MemberProperties();
            const member = publishedData.configuration.Member;
            const partner = publishedData.configuration.Partner;

            // Map member properties with partner id if exist
            if (partner) {
                memberProperties.partner_id = partner.Id;
            }

            memberProperties.login_status = 'not logged in';
            // If member id exist aka member object exist
            if (member && member.Id !== null && member.Id !== undefined && member.Id !== "") {
                memberProperties.login_status = 'logged in';
                memberProperties.customer_id = member.Id;
                memberProperties.member_id = member.Id;
                memberProperties.customer_first_name = member.FirstName;
                memberProperties.customer_last_name = member.LastName;
                memberProperties.customer_type = member.MemberType;
                memberProperties.customer_status = member.Status;
                memberProperties.customer_email = member.EmailAddress ? member.EmailAddress.Address : TealiumConstants.emptyData;

                if (member.PhoneNumbers !== null && member.PhoneNumbers.length > 0 && member.PhoneNumbers[0].Number !== null) {
                    memberProperties.customer_phone = member.PhoneNumbers.first().Number;
                }
                else
                    memberProperties.customer_phone = publishedData.booking.MemberInfo.PhoneNumbers && publishedData.booking.MemberInfo.PhoneNumbers.length > 0 ? (publishedData.booking.MemberInfo.PhoneNumbers.first().Number ? publishedData.booking.MemberInfo.PhoneNumbers.first().Number : TealiumConstants.emptyData) : TealiumConstants.emptyData;


                if (member.Address) {
                    memberProperties.customer_city = member.Address.City;
                    memberProperties.customer_state = member.Address.RegionCode;
                    memberProperties.customer_postal_code = member.Address.PostalCode;
                    memberProperties.customer_country = member.Address.Country;
                }
                memberProperties.customer_dbc_balance = member.DbcBalance.toString();
                memberProperties.customer_vacation_cash_balance = member.VCBalance.toString();
                memberProperties.customer_point_balance = member.PointBalance.toString();
                memberProperties.customer_credits = member.DbcBalance ? member.DbcBalance.toString() : TealiumConstants.emptyData;

                memberProperties.customer_member_class = publishedData.configuration.MemberClassId;
                memberProperties.customer_expiration_date = this.formattedDate(member.ExpirationDate);
                memberProperties.customer_enrollment_date = this.formattedDate(member.EnrollmentDate);
                memberProperties.customer_renewal_price = TealiumConstants.emptyData;
                memberProperties.customer_expiration = member.ExpirationDate ? member.ExpirationDate.toISOString() : TealiumConstants.emptyData;
            }


            memberProperties.customer_type = publishedData.configuration.IsAgent ? 'agent' : 'web';
            if (publishedData.configuration.NameId === "") {
                memberProperties.name_id = publishedData.member.NameId.toString();
            } else {
                memberProperties.name_id = publishedData.configuration.NameId;
            }

            // Populate member properties
            TealiumService.populate(memberProperties, false, false);

            const doProperties = new DisplayOptionsProperties();
            doProperties["dl_display_options.currency_code"] = publishedData.configuration.CurrencyCode;
            doProperties["dl_display_options.Locale"] = publishedData.configuration.Locale;
            TealiumService.populate(doProperties, false, false);
        }
    }

    static updateAgentProperties(publishedData: UserActivityDataContainer) {
        let agentInfo = new AgentProperties();
        if (publishedData.agentInfo !== null && publishedData.agentInfo !== undefined) {
            agentInfo.agent_email = publishedData.agentInfo.agent_email;
            agentInfo.agent_first_name = publishedData.agentInfo.agent_first_name;
            agentInfo.agent_last_name = publishedData.agentInfo.agent_last_name;
            agentInfo.agent_assist_date = this.formattedDateString(publishedData.agentInfo.agent_assist_date);
            agentInfo.agent_phone = publishedData.agentInfo.agent_phone;
            TealiumService.populate(agentInfo, false, false);
        }
    }

    static updateSearchProperties(publishedData: UserActivityDataContainer) {
        let searchProperties = new SearchProperties();
        if (publishedData.searchCriteria) {
            searchProperties.search_city = publishedData.searchCriteria.city
                ? publishedData.searchCriteria.city
                : TealiumConstants.emptyData;
            searchProperties.search_state = publishedData.searchCriteria.state
                ? publishedData.searchCriteria.state
                : TealiumConstants.emptyData;
            searchProperties.search_country = publishedData.searchCriteria.country
                ? publishedData.searchCriteria.country
                : TealiumConstants.emptyData;
            searchProperties.search_destination = publishedData.searchCriteria.searchText
                ? publishedData.searchCriteria.searchText
                : TealiumConstants.emptyData;
            searchProperties.search_month = publishedData.searchCriteria.selectedDate
                ? this.formattedDateMonth(publishedData.searchCriteria.selectedDate)
                : TealiumConstants.emptyData;
            searchProperties.search_type = "Resort";
            searchProperties.search_results = publishedData.searchResults.length.toString();
            searchProperties.selectedDate = publishedData.searchCriteria.selectedDate
                ? this.formattedDateTime(publishedData.searchCriteria.selectedDate)
                : TealiumConstants.emptyData;
            searchProperties.filter = publishedData.searchCriteria.searchFilter
                ? publishedData.searchCriteria.searchFilter
                : TealiumConstants.emptyData;
            TealiumService.populate(searchProperties, false, false);
        }
    }

    static deleteResortDetails(publishedData: UserActivityDataContainer) {

        let resortProperties: ResortProperties = new ResortProperties();
        this.removeData(resortProperties);
    }

    static updateResortDetails(publishedData: UserActivityDataContainer, additionalData: any) {
        
        if (publishedData.searchCriteria &&
            ((!publishedData.searchCriteria.city && !publishedData.searchCriteria.country) ||
                (publishedData.searchCriteria.city === TealiumConstants.emptyData &&
                    publishedData.searchCriteria.country === TealiumConstants.emptyData))) {
            let searchProperties = new SearchProperties();
            searchProperties.search_city = publishedData.resortDetail.city;
            searchProperties.search_state = publishedData.resortDetail.state;
            searchProperties.search_country = publishedData.resortDetail.country;
            searchProperties.search_destination = [publishedData.resortDetail.city] +
                ',' +
                [publishedData.resortDetail.state] +
                ',' +
                [publishedData.resortDetail.country];
            searchProperties.search_type = "Resort";
            searchProperties.search_results = TealiumConstants.emptyData;
            searchProperties.selectedDate = publishedData.searchCriteria.selectedDate
                ? this.formattedDate(publishedData.searchCriteria.selectedDate)
                : TealiumConstants.emptyData;;
            TealiumService.populate(searchProperties, false, false);
        }

        if (publishedData.resortDetail) {
            let resortProperties: ResortProperties = new ResortProperties();
            resortProperties.property_name = publishedData.resortDetail.resortName;
            resortProperties.property_id = publishedData.resortDetail.id;
            resortProperties.property_city = publishedData.resortDetail.city;
            resortProperties.property_state = publishedData.resortDetail.state;
            resortProperties.property_country = publishedData.resortDetail.country;
            resortProperties.property_postal_code = publishedData.resortDetail.zip;
            if (additionalData && additionalData["memberRate"])
                resortProperties.displayed_rate_amount = additionalData["memberRate"].toString();
            else {
                resortProperties.displayed_rate_amount = TealiumConstants.emptyData;
            }
            TealiumService.populate(resortProperties, false, false);
        }
    }

    static deleteCheckoutDetails(publishedData: UserActivityDataContainer) {

        let orderProperties: OrderProperties = new OrderProperties();
        this.removeData(orderProperties);
    }

    static updateCheckoutDetails(publishedData: UserActivityDataContainer, additionalData: any) {

        let calcOrderSubTotal = "";

        if (publishedData.inventory && publishedData.booking) {

            let orderProperties: OrderProperties = new OrderProperties();
            orderProperties.order_points_used = publishedData.booking.Discount.toString();
            orderProperties.order_tax_amount = publishedData.inventory.taxesAndFees.toString();

            calcOrderSubTotal = (publishedData.inventory.priceWithTaxesAndFees -
                publishedData.inventory.taxesAndFees +
                publishedData.inventory.insurance -
                publishedData.booking.Discount +
                publishedData.inventory.certificateRedemptionFee +
                publishedData.booking.AllInclusiveFees).toFixed(2);

            orderProperties.order_sub_total = calcOrderSubTotal;
            orderProperties.order_subtotal = orderProperties.order_sub_total;
            orderProperties.order_special_requests = publishedData.booking.Comments;
            orderProperties.number_of_nights = publishedData.inventory.nights.toString();
            orderProperties.num_of_nights = publishedData.inventory.nights.toString();
            orderProperties.order_store = "Web-Resort Ui";
            orderProperties.checkout_step = "1";
            orderProperties.trip_start_date = this.formattedDate(publishedData.booking.CheckInDate) +
                ' ' +
                publishedData.resortDetail.checkInTime;
            orderProperties.trip_end_date = this.formattedDate(publishedData.booking.CheckOutDate) +
                ' ' +
                publishedData.resortDetail.checkOutTime;
            if (additionalData !== null && additionalData !== undefined) {
                orderProperties.order_grand_total = additionalData["grandTotal"]
                    ? additionalData["grandTotal"].toFixed(2)
                    : TealiumConstants.emptyData;
                orderProperties.total_passengers = additionalData["guestsCount"]
                    ? additionalData["guestsCount"].toString()
                    : TealiumConstants.emptyData;
            } else {
                orderProperties.order_grand_total = calcOrderSubTotal;
            }
            orderProperties.order_cancellation_policy = ["N/A"];
            orderProperties.order_payment_type = publishedData.booking.CreditCardTypeName; //this.getCreditCardTypeName(<CardTypes> Number.parseInt(publishedData.booking.CreditCardTypeName));

            TealiumService.populate(orderProperties, false, false);
        }
    }

    static deleteConfirmationDetails(publishedData: UserActivityDataContainer) {

        if ((<any>window).utag_data['checkout_step']) {
            (<any>window).utag_data['checkout_step'] = TealiumConstants.emptyData;
        }
        if ((<any>window).utag_data['orderSubtotal']) {
            (<any>window).utag_data['orderSubtotal'] = TealiumConstants.emptyData;
        }
    }

    static updateConfirmationDetails(publishedData: UserActivityDataContainer, additionalData: any) {

        let calcOrderSubTotal = "";
        if (publishedData.inventory && publishedData.booking) {
            calcOrderSubTotal = (publishedData.inventory.priceWithTaxesAndFees -
                publishedData.inventory.taxesAndFees +
                publishedData.inventory.insurance -
                publishedData.booking.Discount +
                publishedData.inventory.certificateRedemptionFee +
                publishedData.booking.AllInclusiveFees).toFixed(2);
        }
        if (additionalData !== null && additionalData !== undefined) {
            additionalData["checkout_step"] = "2";
            additionalData["orderSubtotal"] = calcOrderSubTotal;
        }

        TealiumService.populate(additionalData, false, false);
    }

    private static publishDataToUtag(pageName: string, publishedData: UserActivityDataContainer, additionalData: any, pushToTealium: boolean) {
        
        this.updateMemberProperties(publishedData);
        this.updateAgentProperties(publishedData);
        this.updateSearchProperties(publishedData);

        switch (pageName) {
            case ResortPageViews.SearchResults:
                this.deleteResortDetails(publishedData);
                this.deleteConfirmationDetails(publishedData);
                this.deleteCheckoutDetails(publishedData);
                this.update();
                break;
            case ResortPageViews.ResortDetailsStandard:
            case ResortPageViews.ResortDetailsPreferred:
                this.deleteConfirmationDetails(publishedData);
                this.deleteCheckoutDetails(publishedData);
                this.updateResortDetails(publishedData, additionalData);
                this.update();
                break;
            case ResortPageViews.CheckoutPage:
                this.deleteConfirmationDetails(publishedData);
                this.updateCheckoutDetails(publishedData, additionalData);
                this.update();
                break;
            case ResortPageViews.ConfirmationPage:
                this.updateConfirmationDetails(publishedData, additionalData);
                this.update();
                break;
        }
    }


    private static getTealiumPageType(pageName: string) {
        if (pageName === null || pageName === undefined || pageName === "")
            return TealiumConstants.emptyData;
        switch (pageName) {
            case ResortPageViews.SearchResults:
            case ResortPageViews.SearchPage:
                return "Search";
            case ResortPageViews.ResortDetailsPreferred:
            case ResortPageViews.ResortDetailsStandard:
                return "Details";
            case ResortPageViews.CheckoutPage:
                return "Booking_flow";
            case ResortPageViews.ConfirmationPage:
                return "order";
            default:
                return pageName;
        }
    }

    private static getSiteSection(pageName: string) {
        if (pageName === null || pageName === undefined || pageName === "")
            return TealiumConstants.emptyData;
        switch (pageName) {
            case ResortPageViews.SearchResults:
            case ResortPageViews.SearchPage:
                return "Resort Listing";
            case ResortPageViews.ResortDetailsPreferred:
                return "Preferred Resort";
            case ResortPageViews.ResortDetailsStandard:
                return "Standard Resort";
            case ResortPageViews.CheckoutPage:
                return "Booking_flow";
            case ResortPageViews.ConfirmationPage:
                return "Order Confirmation";
            default:
                return pageName;
        }
    }

    private static formattedDate(date: Date) {
        if (this.isValidDate(date)) {
            return moment(date).format("MM/DD/YYYY");
        }
    }

    private static formattedDateMonth(date: Date) {
        if (this.isValidDate(date)) {
            return moment(date).format("MM");
        }
    }

    private static formattedDateString(dateString: string) {
        const date = new Date(dateString);
        return date ? moment(date).format('MM/DD/YYYY') : TealiumConstants.emptyData;
    }

    private static formattedDateTime(date: Date) {
        if (this.isValidDate(date)) {
            return moment(date).format("YYYY/MM/DD HH:mm");
        }
        return "";
    }

    private static isValidDate(d) {
        if (d !== null && d !== undefined) {
            return d instanceof Date && !isNaN(d.getDate());
        }
        return false;
    }

    private static getCreditCardTypeName(cardType: CardTypes) {
        switch (cardType) {
            case CardTypes.Visa:
                return "Visa";
            case CardTypes.MasterCard:
                return "MasterCard";
            case CardTypes.AmericanExpress:
                return "Amex";
            case CardTypes.Discover:
                return "Discover";
            case CardTypes.DinersClub:
                return "DinersClub";
            case CardTypes.MilitaryStarCard:
                return "MilitaryStarCard";
            case CardTypes.NEACreditCard:
                return "NEACreditCard";
            case CardTypes.Sears:
                return "Sears";
            case CardTypes.SearsGiftCard:
                return "SearsGiftCard";
            default:
                return "Other";
        }
    }
}


// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/instrumentation/tealium/tealium.service.ts